import React, { memo, useCallback, useMemo } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Box, TextField,Typography } from "@mui/material";

interface TimeDurationButtonProps {
  Icon: any;
  onClick: () => void;
  disabled: boolean;
}

const TimeDurationButton = ({
  Icon,
  onClick,
  disabled,
}: TimeDurationButtonProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        pointerEvents: disabled ? "none" : "auto",
      }}
      onClick={onClick}
    >
      <Icon
        sx={{
          fontSize: 32,
          cursor: disabled ? "default" : "pointer",
          color: disabled ? "#B9B9B9" : "#676767",
        }}
      />
    </Box>
  );
};

interface TimeDurationFieldProps {
  value: number;
  maxCount: number;
  minCount: number;
  location: string;
  placeholder: string;
  onChange: (newValue: any) => void;
}

const TimeDurationField = ({
  value,
  maxCount,
  minCount,
  location,
  placeholder,
  onChange,
}: TimeDurationFieldProps) => {
  const currentLocation = location && location === "Hours" ? "Hours" : "Minutes";

  const isDecreaseButtonDisabled = useMemo(() => {
    return +value === minCount;
  }, [value]);

  const isIncreaseButtonDisabled = useMemo(() => {
    return +value === maxCount;
  }, [value]);

  const handleDecreaseChange = useCallback(() => {
    onChange(currentLocation === "Hours" ? value - 1 : value === 0 ? 45 : value - 15);
  }, [onChange, value, currentLocation]);

  const handleIncreaseChange = useCallback(() => {
    onChange(currentLocation === "Hours" ? value + 1 : value === 45 ? 0 : value + 15);
  }, [onChange, value, currentLocation]);

  const handleChange = useCallback(
    ({ target: { value } }: any) => {
      const normalizedString = value.replace(/[^0-9]/gi, "");
      if (+normalizedString <= minCount) {
        onChange(minCount);
      } else if (+normalizedString >= maxCount) {
        onChange(maxCount);
      } else {
        onChange(+normalizedString);
      }
    },
    [onChange, maxCount, minCount]
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        margin: "0 23px 0",
      }}
    >
      <TimeDurationButton
        disabled={isDecreaseButtonDisabled}
        Icon={ArrowDropDownIcon}
        onClick={handleDecreaseChange}
      />

      <Typography
        sx={{
          textAlign: "center",
          fontSize: "14px",
          color: "#080808",
          display: "flex",
          alignItems: "center",
        }}
      >
        {value}
      </Typography>

      <TimeDurationButton
        disabled={isIncreaseButtonDisabled}
        Icon={ArrowDropUpIcon}
        onClick={handleIncreaseChange}
      />
    </Box>
  );
};

interface TimeInputProps {
  label: string;
  value: number;
  maxCount: number;
  minCount: number;
  placeholder: string;
  onChange: (newValue: any) => void;
  validationFn?: (value: number) => boolean;
  sx?: any;
}

const TimeInput = memo(({
  label,
  value,
  maxCount,
  minCount,
  placeholder,
  onChange,
  validationFn,
  sx,
}: TimeInputProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #D6D6DB",
        flex: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#F3F3F3",
          color: "#1F1F1F",
          padding: "16px",
          minWidth: 192,
          borderTopLeftRadius: sx?.borderTopLeftRadius,
          borderTopRightRadius: sx?.borderTopRightRadius,
          borderBottomLeftRadius: sx?.borderBottomLeftRadius,
          borderBottomRightRadius: sx?.borderBottomRightRadius,
        }}
      >
        <Typography
          sx={{
            fontWeight: "500",
            textTransform: "capitalize",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          height: "100%",
          alignItems: "center",
          width: 170,
        }}
      >
        <TimeDurationField
          value={value}
          maxCount={maxCount}
          minCount={minCount}
          location={label}
          placeholder={placeholder}
          onChange={onChange}
        />
      </Box>
    </Box>
  );
});

export {TimeInput}
